'use client';

import React, { useState, useMemo } from 'react';
import { Button, Card, Row, Col, Pagination, Spinner, Table, Form, InputGroup, Badge } from 'react-bootstrap';
import { useNavigate, NavLink } from 'react-router';
import { useData } from '../../storage/accessors';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import Icon from '../../components/Icon';
import { faPlus, faTrash, faSearch, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { useScreenSize } from '../../contexts/screenSizeContext';
import useActualWheelId from '@/hooks/useActualWheelId';
import useWheelId from '@/hooks/useWheelId';

const ITEMS_PER_PAGE = 10;

export default function ReflectionPage() {
    const navigate = useNavigate();
    const pathWheelId = useWheelId();
    const wheelId = useActualWheelId();
    const { reflections } = useData();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const screenSize = useScreenSize();
    const isMobile = screenSize === 'MOBILE';

    // Фильтруем и сортируем рефлексии по wheelId и дате создания (новые сверху)
    const allReflections = useMemo(
        () =>
            Object.values(reflections.objects)
                .filter(r => r.wheelId === wheelId)
                .sort((a, b) => b.createdAt - a.createdAt),
        [reflections.objects, wheelId]
    );

    // Поиск по названию
    const filteredReflections = useMemo(() => {
        if (!searchQuery.trim()) return allReflections;
        const query = searchQuery.toLowerCase().trim();
        return allReflections.filter(r =>
            (r.title || '').toLowerCase().includes(query)
        );
    }, [allReflections, searchQuery]);

    // Пагинация
    const totalPages = Math.max(1, Math.ceil(filteredReflections.length / ITEMS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentReflections = filteredReflections.slice(startIndex, endIndex);

    // Обработчики пагинации
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Сброс поиска
    const handleClearSearch = () => {
        setSearchQuery('');
        setCurrentPage(1);
    };

    // Переход к созданию новой рефлексии
    const handleCreate = () => {
        navigate(`/${pathWheelId}/reflection/create`);
    };

    // Удаление рефлексии
    const handleDelete = (uuid: string) => {
        if (window.confirm('Вы уверены, что хотите удалить эту запись рефлексии?')) {
            reflections.delete(uuid);
        }
    };

    // Переход к просмотру рефлексии
    const handleView = (uuid: string) => {
        navigate(`/${pathWheelId}/reflection/${uuid}`);
    };

    if (reflections.isLoading) {
        return (
            <div className="px-3 d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="px-3 py-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h3 className="mb-0">Рефлексия</h3>
                            <p className="text-muted mb-0">
                                Отслеживайте свой прогресс и анализируйте результаты
                            </p>
                        </div>
                        <Button variant="outline-success" onClick={handleCreate}>
                            <Icon icon={faPlus} className="me-2" />
                            Новая рефлексия
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className="mb-3 g-2 align-items-center">
                <Col>
                    <InputGroup>
                        <InputGroup.Text>
                            <Icon icon={faSearch} />
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Поиск по названию…"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            isInvalid={searchQuery.trim() && filteredReflections.length === 0 ? true : undefined}
                        />
                        {searchQuery && (
                            <Button variant="outline-secondary" onClick={handleClearSearch}>
                                <Icon icon={faTimesCircle} />
                            </Button>
                        )}
                    </InputGroup>
                    {searchQuery.trim() && filteredReflections.length === 0 && (
                        <Form.Text className="text-danger">Ничего не найдено</Form.Text>
                    )}
                </Col>
                <Col xs="auto">
                    <Badge bg="secondary">
                        {filteredReflections.length} из {allReflections.length}
                    </Badge>
                </Col>
            </Row>

            {allReflections.length === 0 ? (
                <div className="p-4 text-center rounded" style={{ backgroundColor: 'var(--bs-secondary-bg)' }}>
                    <p className="text-muted mb-0">
                        У вас пока нет записей рефлексии. Создайте первую, чтобы начать отслеживать свой прогресс!
                    </p>
                </div>
            ) : filteredReflections.length === 0 ? null : (
                <>
                    {/* Desktop View - Table */}
                    {!isMobile && (
                        <Table responsive hover className="align-middle">
                            <thead>
                                <tr>
                                    <th style={{ width: '30%' }}>Название</th>
                                    <th style={{ width: '25%' }}>Период</th>
                                    <th style={{ width: '20%' }}>Создано</th>
                                    <th className="text-end" style={{ width: '25%' }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentReflections.map((reflection) => (
                                    <tr key={reflection.uuid}>
                                        <td>
                                            <NavLink
                                                to={`/${pathWheelId}/reflection/${reflection.uuid}`}
                                                className="text-decoration-none text-body"
                                            >
                                                <strong>{reflection.title || 'Без названия'}</strong>
                                            </NavLink>
                                        </td>
                                        <td>
                                            {format(new Date(reflection.dateFrom), 'd MMM yyyy', { locale: ru })}
                                            {' — '}
                                            {format(new Date(reflection.dateTo), 'd MMM yyyy', { locale: ru })}
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {format(new Date(reflection.createdAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                                            </small>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(reflection.uuid)}
                                            >
                                                <Icon icon={faTrash} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    {/* Mobile View - Cards */}
                    {isMobile && (
                        <Row xs={1} className="g-3">
                            {currentReflections.map((reflection) => (
                                <Col key={reflection.uuid}>
                                    <Card className="h-100 shadow-sm">
                                        <Card.Body>
                                            <Card.Title className="mb-2">
                                                <NavLink
                                                    to={`/${pathWheelId}/reflection/${reflection.uuid}`}
                                                    className="text-decoration-none text-body"
                                                >
                                                    {reflection.title || 'Без названия'}
                                                </NavLink>
                                            </Card.Title>
                                            <Card.Subtitle className="mb-3 text-muted">
                                                {format(new Date(reflection.dateFrom), 'd MMMM yyyy', { locale: ru })}
                                                {' — '}
                                                {format(new Date(reflection.dateTo), 'd MMMM yyyy', { locale: ru })}
                                            </Card.Subtitle>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted">
                                                    {format(new Date(reflection.createdAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                                                </small>
                                                <div>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(reflection.uuid)}
                                                    >
                                                        <Icon icon={faTrash} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {totalPages > 1 && (
                        <Pagination className="justify-content-center mt-4 flex-wrap">
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={safePage === 1} />
                            <Pagination.Prev onClick={() => handlePageChange(safePage - 1)} disabled={safePage === 1} />

                            {[...Array(totalPages)].map((_, i) => (
                                <Pagination.Item
                                    key={i + 1}
                                    active={i + 1 === safePage}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            ))}

                            <Pagination.Next onClick={() => handlePageChange(safePage + 1)} disabled={safePage === totalPages} />
                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={safePage === totalPages} />
                        </Pagination>
                    )}
                </>
            )}
        </div>
    );
}
